class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      if (!event.isTrusted) return;
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));

    this.querySelectorAll(".quantity__button").forEach((button) => {
      button.addEventListener("click", this.onQuantityButtonClick.bind(this));
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute("value");
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    if (!event.target) return;

    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    if (event.target) {
      event.target.select();
    }
  }

  validateQuantity(event) {
    if (!event || !event.target) return;

    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    // Restore original sections
    const sections = [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset.id,
        selector: ".js-contents",
      },
    ];

    // Add cart-drawer-items if it exists (for drawer cart type)
    const cartDrawerItems = document.getElementById("CartDrawer-CartItems");
    if (cartDrawerItems) {
      // Restore cart drawer items section
      sections.push({
        id: "CartDrawer-CartItems",
        section: cartDrawerItems.dataset.id, // Assuming drawer items section has data-id
        selector: ".js-contents", // Assuming similar selector
      });
    }

    return sections.filter((section) => section?.section); // Filter out sections where the element/data-id wasn't found
  }

  updateQuantity(line, quantity, name, variantId) {
    console.log("updateQuantity called with:", {
      line,
      quantity,
      name,
      variantId,
    });

    this.enableLoading(line);

    // Extract the variant ID from the line string (format: variantId:hash)
    const id = line.includes(":") ? line.split(":")[0] : line;

    const body = JSON.stringify({
      id, // Use id (variant ID) instead of line
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        // --- Start Revert Logic ---
        // Find the potentially updated quantity element again after re-render
        // Use the pure variant ID (`id`) for lookup, as the re-rendered HTML might use that
        const currentQuantityElement =
          document.getElementById(`Quantity-${id}`) ||
          document.getElementById(`Drawer-quantity-${id}`);

        const serverItem = parsedState.items.find(
          (item) => item.variant_id.toString() === id
        );
        const updatedValue = serverItem?.quantity ?? 0;

        if (!currentQuantityElement && updatedValue > 0) {
          // Item was re-added unexpectedly? This case might need specific handling if it occurs.
          console.warn(
            "Cart item re-appeared unexpectedly after update attempt."
          );
          console.warn(
            `Variant ID: ${id}, Expected Element IDs: Quantity-${id} or Drawer-quantity-${id}`
          );
        } else if (
          currentQuantityElement &&
          parseInt(currentQuantityElement.value) !== updatedValue
        ) {
          // If server state quantity differs from optimistic UI state, revert UI
          console.log(
            `Quantity mismatch for ${line}. Optimistic: ${currentQuantityElement.value}, Server: ${updatedValue}. Reverting.`
          );
          currentQuantityElement.value = updatedValue; // Set to actual server value

          let message = window.cartStrings.quantityError.replace(
            "[quantity]",
            updatedValue
          );
          // Show quantity error only if the updated value isn't 0 (item removal)
          this.updateLiveRegions(line, updatedValue > 0 ? message : "");
        } else {
          // Quantity matches or item removed (updatedValue is 0 and element might be gone)
          // Clear any previous error messages for this line if it still exists
          this.updateLiveRegions(line, "");
        }
        // --- End Revert Logic ---

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.error("Error updating cart:", error);

        // Revert optimistic update on error
        if (quantityElement && originalValue !== null) {
          console.log(
            `Fetch error for ${line}. Reverting to original value: ${originalValue}`
          );
          quantityElement.value = originalValue;
        }

        // Display general error message
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const escapedLineId = CSS.escape(line);

    // Optional: Remove disabling the entire container for potentially smoother UI
    /*
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");
    */

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${escapedLineId} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${escapedLineId} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
  }

  disableLoading(line) {
    const escapedLineId = CSS.escape(line);

    // Optional: Remove disabling the entire container for potentially smoother UI
    /*
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");
    */

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${escapedLineId} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${escapedLineId} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }

  onQuantityButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const input = button.closest(".quantity").querySelector(".quantity__input");
    const currentValue = parseInt(input.value);
    const isPlus = button.name.includes("plus");
    const newValue = isPlus ? currentValue + 1 : currentValue - 1;

    // Basic validation before optimistic update
    const min = parseInt(input.min);
    const max = input.hasAttribute("max") ? parseInt(input.max) : null;

    if (newValue < min) {
      console.log("Attempted to go below min value.");
      // Optionally show a brief visual cue or do nothing
      return; // Don't proceed with optimistic update or fetch
    }

    if (max !== null && newValue > max) {
      const message = window.cartStrings.quantityError.replace(
        "[quantity]",
        max
      );
      console.log("Attempted to go above max value.");
      this.setValidity({ target: input }, input.dataset.index, message);
      // Optionally show brief visual cue or do nothing further than showing validity message
      return; // Don't proceed if clearly exceeding max
    }

    // Optimistic UI update
    console.log(`Optimistically setting ${input.dataset.index} to ${newValue}`);
    input.value = newValue;

    // Call updateQuantity to sync with server
    this.updateQuantity(
      input.dataset.index,
      newValue,
      button.name,
      input.dataset.quantityVariantId
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
